import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Save, FileText, Clock, User, Target } from 'lucide-react';
import { toast } from 'sonner';
import type { AnalysisResult } from '@/lib/analysis-engine';

interface GameState {
  position: string;
  moves: string[];
  currentMoveIndex: number;
}

interface SavedAnalysisGame {
  id: string;
  title: string;
  description?: string;
  gameState: GameState;
  analysis?: AnalysisResult;
  savedAt: string;
  tags: string[];
  playerNames: {
    white: string;
    black: string;
  };
  result?: 'white' | 'black' | 'draw';
  event?: string;
  site?: string;
  date?: string;
}

interface EnhancedSaveGameSystemProps {
  gameState: GameState;
  analysis?: AnalysisResult | null;
  onGameSaved?: (game: SavedAnalysisGame) => void;
}

export function EnhancedSaveGameSystem({ 
  gameState, 
  analysis, 
  onGameSaved 
}: EnhancedSaveGameSystemProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    whitePlayer: 'White',
    blackPlayer: 'Black',
    result: '',
    event: '',
    site: 'ChessHatch Analysis',
    tags: ''
  });

  const saveGame = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a game title');
      return;
    }

    setSaving(true);
    
    try {
      const gameToSave: SavedAnalysisGame = {
        id: `analysis-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        gameState: {
          ...gameState,
          position: gameState.position || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
        },
        analysis: analysis || undefined,
        savedAt: new Date().toISOString(),
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        playerNames: {
          white: formData.whitePlayer.trim() || 'White',
          black: formData.blackPlayer.trim() || 'Black'
        },
        result: formData.result as 'white' | 'black' | 'draw' | undefined,
        event: formData.event.trim() || undefined,
        site: formData.site.trim() || 'ChessHatch Analysis',
        date: new Date().toISOString().split('T')[0]
      };

      // Save to localStorage
      const existingGames = JSON.parse(localStorage.getItem('chesshatch_analysis_games') || '[]');
      const updatedGames = [gameToSave, ...existingGames];
      localStorage.setItem('chesshatch_analysis_games', JSON.stringify(updatedGames));

      // Also save to the regular games list for backward compatibility
      const regularGames = JSON.parse(localStorage.getItem('chesshatch_games') || '[]');
      const regularGame = {
        id: gameToSave.id,
        opponent: gameToSave.playerNames.black,
        result: gameToSave.result === 'white' ? 'win' : gameToSave.result === 'black' ? 'loss' : 'draw',
        date: gameToSave.savedAt,
        moves: gameToSave.gameState.moves,
        gameState: gameToSave.gameState,
        userColor: 'white',
        duration: undefined
      };
      regularGames.unshift(regularGame);
      localStorage.setItem('chesshatch_games', JSON.stringify(regularGames));

      toast.success('Analysis saved successfully!');
      onGameSaved?.(gameToSave);
      setIsDialogOpen(false);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        whitePlayer: 'White',
        blackPlayer: 'Black',
        result: '',
        event: '',
        site: 'ChessHatch Analysis',
        tags: ''
      });
      
    } catch (error) {
      console.error('Error saving game:', error);
      toast.error('Failed to save analysis');
    } finally {
      setSaving(false);
    }
  };

  const generateAutoTitle = () => {
    const moveCount = Math.ceil(gameState.moves.length / 2);
    const hasAnalysis = analysis ? ' (Analyzed)' : '';
    return `Analysis Game - ${moveCount} moves${hasAnalysis}`;
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="default" 
          className="gap-2"
          disabled={!gameState.moves.length}
        >
          <Save className="h-4 w-4" />
          Save Analysis
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Save Game Analysis
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Game Summary */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span>{gameState.moves.length} moves</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{new Date().toLocaleDateString()}</span>
                </div>
                {analysis && (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      Depth {analysis.depth}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Game Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter game title..."
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, title: generateAutoTitle() }))}
                  className="text-xs"
                >
                  Generate Title
                </Button>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="result">Result</Label>
                <select
                  id="result"
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  value={formData.result}
                  onChange={(e) => setFormData(prev => ({ ...prev, result: e.target.value }))}
                >
                  <option value="">Select result...</option>
                  <option value="white">White wins</option>
                  <option value="black">Black wins</option>
                  <option value="draw">Draw</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="whitePlayer">White Player</Label>
                <Input
                  id="whitePlayer"
                  value={formData.whitePlayer}
                  onChange={(e) => setFormData(prev => ({ ...prev, whitePlayer: e.target.value }))}
                  placeholder="White player name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="blackPlayer">Black Player</Label>
                <Input
                  id="blackPlayer"
                  value={formData.blackPlayer}
                  onChange={(e) => setFormData(prev => ({ ...prev, blackPlayer: e.target.value }))}
                  placeholder="Black player name"
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="event">Event/Tournament</Label>
                <Input
                  id="event"
                  value={formData.event}
                  onChange={(e) => setFormData(prev => ({ ...prev, event: e.target.value }))}
                  placeholder="Tournament or event name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="site">Location</Label>
                <Input
                  id="site"
                  value={formData.site}
                  onChange={(e) => setFormData(prev => ({ ...prev, site: e.target.value }))}
                  placeholder="Playing location"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="opening, endgame, blunder, brilliant..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Notes & Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add notes about the game, key moments, or analysis insights..."
                rows={3}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setIsDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={saveGame}
              disabled={saving || !formData.title.trim()}
              className="gap-2"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Analysis
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}